import Link from "../shared-components/icons/Link";
import { SocialIcon } from "../utils/social-icon";
import { getDisplayDate } from "./helpers";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import {
  InformationCircleIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { getSocialMediaFromUrl } from "../utils";
import ReactTooltip from "react-tooltip";
import { ItemWithMetaData } from "../api/Items";
import { getDisplayPostTypeText } from "../utils/post-utils";

type SystemMediaPostHeaderProps = {
  postUrl: string;
  profileUrl: string;
  datePosted: string;
  showLink?: boolean;
  isReadOnly: boolean;
  hideRemove?: boolean;
  handleOnRemove?: () => void;
  handleOnEdit?: () => void;
  post?: ItemWithMetaData;
};

const SystemMediaPostHeader = ({
  postUrl,
  profileUrl,
  datePosted,
  showLink,
  isReadOnly,
  handleOnRemove,
  handleOnEdit,
  hideRemove,
  post,
}: SystemMediaPostHeaderProps) => {
  const postedOnText: string = getSocialMediaFromUrl(profileUrl);
  const displayDate: string = getDisplayDate(datePosted);
  const postTypeText = post ? getDisplayPostTypeText(post) : "Posted";

  return (
    <div className="flex flex-nowrap items-center mb-3">
      <SocialIcon url={postUrl} />
      <div className="flex flex-col">
        {postedOnText && (
          <div className="flex items-center text-sm leading-5 font-medium mx-3">
            {`${postTypeText} on ${postedOnText}`}
            {showLink && (
              <a
                href={postUrl}
                target="_blank"
                rel="noreferrer"
                className="ml-4 inline-flex items-center"
              >
                <Link />
                <span className="text-xs leading-4 font-medium pl-2">
                  View Link
                </span>
              </a>
            )}
          </div>
        )}
        {displayDate && (
          <div className="text-sm leading-5 font-normal mx-3">
            {displayDate}
          </div>
        )}
      </div>
      <div className="flex ml-auto">
        {!isReadOnly && (
          <div className="flex gap-2">
            {!hideRemove && (
              <Button
                text={
                  <span className="flex items-center gap-2">
                    <TrashIcon className="inline-flex w-5" />
                    <span className="text-xs leading-4 font-medium">
                      Remove
                    </span>
                  </span>
                }
                onClick={handleOnRemove}
                colorType={ButtonColorTypes.custom}
                className="hover:bg-slate-50"
              />
            )}
            {hideRemove && (
              <>
                <span
                  data-tip={
                    "This post cannot be remove because it contains keyword(s)"
                  }
                  className="flex items-center cursor-pointer"
                >
                  <InformationCircleIcon height={24} width={24} color="grey" />
                </span>
                <ReactTooltip
                  className="hover:visible:opacity-1 w-96"
                  delayHide={1000}
                  effect="solid"
                  html={true}
                />
              </>
            )}
            <Button
              text={
                <span className="flex items-center gap-2">
                  <PencilIcon className="inline-flex w-5" />
                  <span className="text-xs leading-4 font-medium">Edit</span>
                </span>
              }
              onClick={handleOnEdit}
              colorType={ButtonColorTypes.custom}
              className="bg-green-50 text-green-700 w-24"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemMediaPostHeader;
